import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, AlertCircleIcon, SaveIcon, SendIcon, FileTextIcon, UserIcon, BookOpenIcon, UploadIcon, ClipboardCheckIcon, SparklesIcon, InfoIcon, HelpCircleIcon, XIcon, ChevronRightIcon, CheckIcon, CalendarIcon, GraduationCapIcon, MailIcon, PhoneIcon, MapPinIcon, GlobeIcon, BuildingIcon, FileIcon, PaperclipIcon, PlusCircleIcon } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
// Sample program data - in a real app, this would come from an API
const samplePrograms = [{
  id: 'PRG001',
  name: 'Master of Business Administration',
  type: "Master's",
  duration: '2 Years',
  university: 'UAE Business School',
  location: 'Dubai, UAE',
  applicationDeadline: 'June 30, 2023',
  nextIntake: 'September 2023',
  requirements: {
    minGPA: 3.0,
    degreeLevel: "Bachelor's",
    englishProficiency: {
      ielts: 6.5,
      toefl: 90
    },
    workExperience: 2,
    requiredDocuments: ['Academic Transcripts', 'CV/Resume', 'Statement of Purpose', 'Two Reference Letters', 'English Proficiency Test Results', 'Passport Copy']
  }
}
// Other programs would be here
];
// Sample user profile data - in a real app, this would come from an API or auth context
const userProfile = {
  firstName: 'Sarah',
  lastName: 'Ahmed',
  email: 'sarah@example.com',
  phone: '+971 50 123 4567',
  nationality: 'UAE',
  dateOfBirth: '1995-05-15',
  currentAddress: {
    street: '123 Sheikh Zayed Road',
    city: 'Dubai',
    country: 'UAE',
    postalCode: '12345'
  },
  education: [{
    institution: 'Dubai University',
    degree: "Bachelor's in Business",
    fieldOfStudy: 'Business Administration',
    startDate: '2015-09-01',
    endDate: '2019-06-30',
    gpa: 3.7,
    maxGpa: 4.0
  }],
  workExperience: [{
    company: 'Dubai Holdings',
    position: 'Business Analyst',
    startDate: '2019-09-01',
    endDate: 'Present',
    description: 'Analyzing business processes and implementing improvements'
  }],
  documents: [{
    id: 'DOC001',
    name: 'Academic Transcript',
    type: 'PDF',
    status: 'Verified',
    uploadDate: '2023-05-01'
  }, {
    id: 'DOC002',
    name: 'Passport Copy',
    type: 'PDF',
    status: 'Verified',
    uploadDate: '2023-05-01'
  }, {
    id: 'DOC003',
    name: 'CV/Resume',
    type: 'PDF',
    status: 'Verified',
    uploadDate: '2023-05-03'
  }]
};
// Progress Step component
const ProgressStep = ({
  currentStep,
  totalSteps,
  steps
}) => {
  return <div className="w-full mb-8" data-id="element-3309">
      <div className="flex justify-between items-center mb-2" data-id="element-3310">
        {steps.map((step, index) => <div key={index} className="flex flex-col items-center relative" data-id="element-3311">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${index < currentStep ? 'bg-green-500 text-white' : index === currentStep ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-3312">
              {index < currentStep ? <CheckIcon className="w-5 h-5" data-id="element-3313" /> : <span data-id="element-3314">{index + 1}</span>}
            </div>
            <span className={`text-xs mt-2 text-center w-24 ${index <= currentStep ? 'text-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-3315">
              {step}
            </span>
          </div>)}
      </div>
      <div className="relative w-full h-2 bg-gray-200 rounded-full mt-2" data-id="element-3316">
        <div className="absolute h-2 bg-primary-500 rounded-full transition-all duration-300" style={{
        width: `${currentStep / (totalSteps - 1) * 100}%`
      }} data-id="element-3317"></div>
      </div>
    </div>;
};
// AI Recommendation component
const AIRecommendation = ({
  recommendations,
  onClose
}) => {
  if (recommendations.length === 0) return null;
  return;
};
// Eligibility Alert component
const EligibilityAlert = ({
  isEligible,
  issues
}) => {
  if (isEligible && issues.length === 0) return null;
  return <div className={`${isEligible ? 'bg-yellow-50 border-yellow-100' : 'bg-red-50 border-red-100'} border rounded-lg p-4 mb-6`} data-id="element-3318">
      <div className="flex items-start" data-id="element-3319">
        <AlertCircleIcon className={`w-5 h-5 ${isEligible ? 'text-yellow-500' : 'text-red-500'} mr-3 flex-shrink-0 mt-0.5`} data-id="element-3320" />
        <div data-id="element-3321">
          <h3 className={`font-medium ${isEligible ? 'text-yellow-800' : 'text-red-800'} mb-2`} data-id="element-3322">
            {isEligible ? 'Application Warning' : 'Eligibility Issues'}
          </h3>
          <ul className="space-y-2" data-id="element-3323">
            {issues.map((issue, index) => <li key={index} className={`text-sm ${isEligible ? 'text-yellow-700' : 'text-red-700'} flex items-start`} data-id="element-3324">
                <AlertCircleIcon className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" data-id="element-3325" />
                <span data-id="element-3326">{issue}</span>
              </li>)}
          </ul>
        </div>
      </div>
    </div>;
};
// Form field with validation
const FormField = ({
  label,
  type = 'text',
  name,
  value,
  onChange,
  required = false,
  error = null,
  placeholder = '',
  options = [],
  helpText = null
}) => {
  return <div className="mb-4" data-id="element-3327">
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3328">
        {label} {required && <span className="text-red-500" data-id="element-3329">*</span>}
      </label>
      {type === 'select' ? <select id={name} name={name} value={value} onChange={onChange} className={`w-full p-2.5 border rounded-md ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:ring-primary-500 focus:border-primary-500`} required={required} data-id="element-3330">
          <option value="" data-id="element-3331">Select {label}</option>
          {options.map((option, index) => <option key={index} value={option.value || option} data-id="element-3332">
              {option.label || option}
            </option>)}
        </select> : type === 'textarea' ? <textarea id={name} name={name} value={value} onChange={onChange} placeholder={placeholder} rows={4} className={`w-full p-2.5 border rounded-md ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:ring-primary-500 focus:border-primary-500`} required={required} data-id="element-3333" /> : type === 'date' ? <div className="relative" data-id="element-3334">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3335">
            <CalendarIcon className="w-4 h-4 text-gray-500" data-id="element-3336" />
          </div>
          <input id={name} type="date" name={name} value={value} onChange={onChange} className={`w-full p-2.5 pl-10 border rounded-md ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:ring-primary-500 focus:border-primary-500`} required={required} data-id="element-3337" />
        </div> : <div className="relative" data-id="element-3338">
          {type === 'email' && <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3339">
              <MailIcon className="w-4 h-4 text-gray-500" data-id="element-3340" />
            </div>}
          {type === 'tel' && <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3341">
              <PhoneIcon className="w-4 h-4 text-gray-500" data-id="element-3342" />
            </div>}
          <input id={name} type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} className={`w-full p-2.5 ${type === 'email' || type === 'tel' ? 'pl-10' : ''} border rounded-md ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:ring-primary-500 focus:border-primary-500`} required={required} data-id="element-3343" />
        </div>}
      {helpText && <p className="mt-1 text-xs text-gray-500" data-id="element-3344">{helpText}</p>}
      {error && <p className="mt-1 text-xs text-red-600" data-id="element-3345">{error}</p>}
    </div>;
};
// Modified DocumentUpload component to receive formData as a prop
const DocumentUpload = ({
  label,
  required = false,
  description = '',
  onUpload,
  existingDocument = null,
  documents = {} // Add documents prop with default empty object
}) => {
  const [isDragging, setIsDragging] = useState(false);
  // Check if we have a pre-populated document for this label
  const documentKey = label.replace(/\s+/g, '');
  const prePopulatedDoc = documents[documentKey]; // Use documents prop instead of formData
  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onUpload(e.dataTransfer.files[0]);
    }
  };
  return <div className="mb-6" data-id="element-3346">
      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3347">
        {label} {required && <span className="text-red-500" data-id="element-3348">*</span>}
      </label>
      {existingDocument || prePopulatedDoc ? <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50" data-id="element-3349">
          <div className="flex items-center" data-id="element-3350">
            <FileIcon className="w-5 h-5 text-gray-500 mr-3" data-id="element-3351" />
            <div data-id="element-3352">
              <p className="text-sm font-medium text-gray-800" data-id="element-3353">
                {existingDocument ? existingDocument.name : prePopulatedDoc.name}
              </p>
              <p className="text-xs text-gray-500" data-id="element-3354">
                Uploaded on{' '}
                {existingDocument ? existingDocument.uploadDate : prePopulatedDoc.uploadDate}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2" data-id="element-3355">
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-3356">
              {existingDocument ? existingDocument.status : prePopulatedDoc.status}
            </span>
            <button className="text-primary-500 hover:text-primary-700 text-sm font-medium" data-id="element-3357">
              Replace
            </button>
          </div>
        </div> : <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-400'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => document.getElementById(`file-upload-${label}`).click()} data-id="element-3358">
          <input id={`file-upload-${label}`} type="file" className="hidden" onChange={e => onUpload(e.target.files[0])} data-id="element-3359" />
          <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-3360" />
          <p className="text-sm text-gray-600 mb-1" data-id="element-3361">
            Drag and drop your file here or{' '}
            <span className="text-primary-500 font-medium" data-id="element-3362">browse</span>
          </p>
          <p className="text-xs text-gray-500 mb-1" data-id="element-3363">{description}</p>
          <p className="text-xs text-gray-400" data-id="element-3364">PDF, JPG or PNG (Max. 5MB)</p>
        </div>}
    </div>;
};
// Education Item component
const EducationItem = ({
  education,
  index,
  onChange,
  onRemove,
  errors = {}
}) => {
  return <div className="p-4 border border-gray-200 rounded-lg mb-4" data-id="element-3365">
      <div className="flex justify-between items-center mb-4" data-id="element-3366">
        <h3 className="text-md font-medium text-gray-800" data-id="element-3367">
          Education #{index + 1}
        </h3>
        {index > 0 && <button type="button" onClick={() => onRemove(index)} className="text-red-500 hover:text-red-700 text-sm" data-id="element-3368">
            Remove
          </button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3369">
        <FormField label="Institution" name={`institution-${index}`} value={education.institution} onChange={e => onChange(index, 'institution', e.target.value)} required error={errors[`education.${index}.institution`]} data-id="element-3370" />
        <FormField label="Degree Level" type="select" name={`degreeLevel-${index}`} value={education.degreeLevel} onChange={e => onChange(index, 'degreeLevel', e.target.value)} required options={["Associate's", "Bachelor's", "Master's", 'Doctorate', 'Diploma', 'Certificate']} error={errors[`education.${index}.degreeLevel`]} data-id="element-3371" />
        <FormField label="Field of Study" name={`fieldOfStudy-${index}`} value={education.fieldOfStudy} onChange={e => onChange(index, 'fieldOfStudy', e.target.value)} required error={errors[`education.${index}.fieldOfStudy`]} data-id="element-3372" />
        <div className="grid grid-cols-2 gap-4" data-id="element-3373">
          <FormField label="Start Date" type="date" name={`startDate-${index}`} value={education.startDate} onChange={e => onChange(index, 'startDate', e.target.value)} required error={errors[`education.${index}.startDate`]} data-id="element-3374" />
          <FormField label="End Date" type="date" name={`endDate-${index}`} value={education.endDate} onChange={e => onChange(index, 'endDate', e.target.value)} error={errors[`education.${index}.endDate`]} helpText="Leave blank if current" data-id="element-3375" />
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-3376">
          <FormField label="GPA" type="number" name={`gpa-${index}`} value={education.gpa} onChange={e => onChange(index, 'gpa', e.target.value)} required error={errors[`education.${index}.gpa`]} helpText="Your GPA" data-id="element-3377" />
          <FormField label="Max GPA" type="number" name={`maxGpa-${index}`} value={education.maxGpa} onChange={e => onChange(index, 'maxGpa', e.target.value)} required error={errors[`education.${index}.maxGpa`]} helpText="Scale (e.g., 4.0)" data-id="element-3378" />
        </div>
      </div>
    </div>;
};
// Work Experience Item component
const WorkExperienceItem = ({
  experience,
  index,
  onChange,
  onRemove,
  errors = {}
}) => {
  return <div className="p-4 border border-gray-200 rounded-lg mb-4" data-id="element-3379">
      <div className="flex justify-between items-center mb-4" data-id="element-3380">
        <h3 className="text-md font-medium text-gray-800" data-id="element-3381">
          Work Experience #{index + 1}
        </h3>
        {index > 0 && <button type="button" onClick={() => onRemove(index)} className="text-red-500 hover:text-red-700 text-sm" data-id="element-3382">
            Remove
          </button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3383">
        <FormField label="Company" name={`company-${index}`} value={experience.company} onChange={e => onChange(index, 'company', e.target.value)} required error={errors[`workExperience.${index}.company`]} data-id="element-3384" />
        <FormField label="Position" name={`position-${index}`} value={experience.position} onChange={e => onChange(index, 'position', e.target.value)} required error={errors[`workExperience.${index}.position`]} data-id="element-3385" />
        <div className="grid grid-cols-2 gap-4" data-id="element-3386">
          <FormField label="Start Date" type="date" name={`startDate-${index}`} value={experience.startDate} onChange={e => onChange(index, 'startDate', e.target.value)} required error={errors[`workExperience.${index}.startDate`]} data-id="element-3387" />
          <FormField label="End Date" type="date" name={`endDate-${index}`} value={experience.endDate} onChange={e => onChange(index, 'endDate', e.target.value)} error={errors[`workExperience.${index}.endDate`]} helpText="Leave blank if current" data-id="element-3388" />
        </div>
        <div className="md:col-span-2" data-id="element-3389">
          <FormField label="Description" type="textarea" name={`description-${index}`} value={experience.description} onChange={e => onChange(index, 'description', e.target.value)} placeholder="Describe your responsibilities and achievements" error={errors[`workExperience.${index}.description`]} data-id="element-3390" />
        </div>
      </div>
    </div>;
};
// Main Application Form component
const ApplicationForm = () => {
  const {
    programId
  } = useParams();
  const navigate = useNavigate();
  // State for the program and form data
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  // Pre-populated document data for demo purposes
  const prePopulatedDocuments = {
    AcademicTranscripts: {
      name: 'sample-transcript.pdf',
      size: 1024000,
      type: 'application/pdf',
      uploadDate: '2023-05-03',
      status: 'Verified'
    },
    CVResume: {
      name: 'sample-resume.pdf',
      size: 512000,
      type: 'application/pdf',
      uploadDate: '2023-05-03',
      status: 'Verified'
    },
    StatementofPurpose: {
      name: 'sample-statement.pdf',
      size: 728000,
      type: 'application/pdf',
      uploadDate: '2023-05-04',
      status: 'Verified'
    },
    TwoReferenceLetters: {
      name: 'sample-reference-letters.pdf',
      size: 896000,
      type: 'application/pdf',
      uploadDate: '2023-05-04',
      status: 'Verified'
    },
    EnglishProficiencyTestResults: {
      name: 'sample-ielts-results.pdf',
      size: 645000,
      type: 'application/pdf',
      uploadDate: '2023-05-05',
      status: 'Verified'
    }
  };
  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: userProfile.firstName || '',
      lastName: userProfile.lastName || '',
      email: userProfile.email || '',
      phone: userProfile.phone || '',
      dateOfBirth: userProfile.dateOfBirth || '',
      nationality: userProfile.nationality || '',
      address: {
        street: userProfile.currentAddress?.street || '',
        city: userProfile.currentAddress?.city || '',
        country: userProfile.currentAddress?.country || '',
        postalCode: userProfile.currentAddress?.postalCode || ''
      }
    },
    education: userProfile.education || [{
      institution: '',
      degreeLevel: '',
      fieldOfStudy: '',
      startDate: '',
      endDate: '',
      gpa: '',
      maxGpa: ''
    }],
    workExperience: userProfile.workExperience || [{
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      description: ''
    }],
    documents: prePopulatedDocuments,
    englishProficiency: {
      type: '',
      score: ''
    },
    additionalInfo: ''
  });
  // State for validation and recommendations
  const [errors, setErrors] = useState({});
  const [eligibilityIssues, setEligibilityIssues] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(true);
  // Define the steps
  const steps = ['Personal Information', 'Academic Background', 'Work Experience', 'Documents', 'Review & Submit'];
  // Fetch the program data
  useEffect(() => {
    // In a real app, this would be an API call
    const fetchProgram = () => {
      setLoading(true);
      // Find the program with the matching ID
      const foundProgram = samplePrograms.find(p => p.id === programId);
      if (foundProgram) {
        setProgram(foundProgram);
        // Check for existing documents that match required documents
        const documentRecommendations = [];
        foundProgram.requirements.requiredDocuments.forEach(doc => {
          const existingDoc = userProfile.documents.find(d => d.name.toLowerCase().includes(doc.toLowerCase()));
          if (!existingDoc) {
            documentRecommendations.push(`Upload your ${doc} to complete your application.`);
          }
        });
        if (documentRecommendations.length > 0) {
          setRecommendations(documentRecommendations);
        }
      }
      setLoading(false);
    };
    fetchProgram();
  }, [programId]);
  // Handle form field changes
  const handleChange = (section, field, value) => {
    setFormData(prevData => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: value
      }
    }));
    // Clear any error for this field
    if (errors[`${section}.${field}`]) {
      setErrors(prevErrors => {
        const newErrors = {
          ...prevErrors
        };
        delete newErrors[`${section}.${field}`];
        return newErrors;
      });
    }
    // Validate eligibility in real-time for certain fields
    validateEligibility(section, field, value);
  };
  // Handle nested field changes (like address)
  const handleNestedChange = (section, parent, field, value) => {
    setFormData(prevData => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [parent]: {
          ...prevData[section][parent],
          [field]: value
        }
      }
    }));
    // Clear any error for this field
    if (errors[`${section}.${parent}.${field}`]) {
      setErrors(prevErrors => {
        const newErrors = {
          ...prevErrors
        };
        delete newErrors[`${section}.${parent}.${field}`];
        return newErrors;
      });
    }
  };
  // Handle education changes
  const handleEducationChange = (index, field, value) => {
    setFormData(prevData => {
      const newEducation = [...prevData.education];
      newEducation[index] = {
        ...newEducation[index],
        [field]: value
      };
      return {
        ...prevData,
        education: newEducation
      };
    });
    // Clear any error for this field
    if (errors[`education.${index}.${field}`]) {
      setErrors(prevErrors => {
        const newErrors = {
          ...prevErrors
        };
        delete newErrors[`education.${index}.${field}`];
        return newErrors;
      });
    }
    // Validate GPA eligibility in real-time
    if (field === 'gpa' || field === 'maxGpa') {
      validateGPAEligibility(index, field, value);
    }
    // Validate degree level eligibility
    if (field === 'degreeLevel') {
      validateDegreeLevelEligibility(value);
    }
  };
  // Handle work experience changes
  const handleWorkExperienceChange = (index, field, value) => {
    setFormData(prevData => {
      const newWorkExperience = [...prevData.workExperience];
      newWorkExperience[index] = {
        ...newWorkExperience[index],
        [field]: value
      };
      return {
        ...prevData,
        workExperience: newWorkExperience
      };
    });
    // Clear any error for this field
    if (errors[`workExperience.${index}.${field}`]) {
      setErrors(prevErrors => {
        const newErrors = {
          ...prevErrors
        };
        delete newErrors[`workExperience.${index}.${field}`];
        return newErrors;
      });
    }
  };
  // Add education item
  const addEducation = () => {
    setFormData(prevData => ({
      ...prevData,
      education: [...prevData.education, {
        institution: '',
        degreeLevel: '',
        fieldOfStudy: '',
        startDate: '',
        endDate: '',
        gpa: '',
        maxGpa: ''
      }]
    }));
  };
  // Remove education item
  const removeEducation = index => {
    setFormData(prevData => {
      const newEducation = [...prevData.education];
      newEducation.splice(index, 1);
      return {
        ...prevData,
        education: newEducation
      };
    });
  };
  // Add work experience item
  const addWorkExperience = () => {
    setFormData(prevData => ({
      ...prevData,
      workExperience: [...prevData.workExperience, {
        company: '',
        position: '',
        startDate: '',
        endDate: '',
        description: ''
      }]
    }));
  };
  // Remove work experience item
  const removeWorkExperience = index => {
    setFormData(prevData => {
      const newWorkExperience = [...prevData.workExperience];
      newWorkExperience.splice(index, 1);
      return {
        ...prevData,
        workExperience: newWorkExperience
      };
    });
  };
  // Handle document upload
  const handleDocumentUpload = (documentType, file) => {
    // In a real app, you would upload the file to a server
    // For now, we'll just update the state
    setFormData(prevData => ({
      ...prevData,
      documents: {
        ...prevData.documents,
        [documentType]: {
          name: file.name,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString().split('T')[0]
        }
      }
    }));
    // Remove any document recommendations for this document type
    const documentName = documentType.replace(/([A-Z])/g, ' $1').trim();
    setRecommendations(prevRecs => prevRecs.filter(rec => !rec.includes(documentName)));
  };
  // Validate eligibility in real-time
  const validateEligibility = (section, field, value) => {
    if (!program) return;
    // Validate English proficiency
    if (section === 'englishProficiency') {
      if (field === 'type' && value) {
        const scoreField = formData.englishProficiency.score;
        if (scoreField) {
          validateEnglishProficiency(value, scoreField);
        }
      } else if (field === 'score' && value) {
        const typeField = formData.englishProficiency.type;
        if (typeField) {
          validateEnglishProficiency(typeField, value);
        }
      }
    }
  };
  // Validate GPA eligibility
  const validateGPAEligibility = (index, field, value) => {
    if (!program) return;
    const education = formData.education[index];
    const gpa = field === 'gpa' ? parseFloat(value) : parseFloat(education.gpa);
    const maxGpa = field === 'maxGpa' ? parseFloat(value) : parseFloat(education.maxGpa);
    // Only validate if both GPA and maxGPA are provided
    if (!isNaN(gpa) && !isNaN(maxGpa) && maxGpa > 0) {
      const normalizedGpa = gpa / maxGpa * 4.0; // Normalize to 4.0 scale
      if (normalizedGpa < program.requirements.minGPA) {
        // Check if this issue already exists
        if (!eligibilityIssues.some(issue => issue.includes('GPA requirement'))) {
          setEligibilityIssues(prevIssues => [...prevIssues, `Your GPA (${normalizedGpa.toFixed(2)} on a 4.0 scale) is below the minimum requirement of ${program.requirements.minGPA}.`]);
          // Add recommendation
          setRecommendations(prevRecs => [...prevRecs, `Consider including additional achievements or certifications to strengthen your application.`]);
        }
      } else {
        // Remove the GPA issue if it exists
        setEligibilityIssues(prevIssues => prevIssues.filter(issue => !issue.includes('GPA requirement')));
        // Remove the related recommendation
        setRecommendations(prevRecs => prevRecs.filter(rec => !rec.includes('additional achievements')));
      }
    }
  };
  // Validate degree level eligibility
  const validateDegreeLevelEligibility = value => {
    if (!program || !value) return;
    const requiredLevel = program.requirements.degreeLevel;
    // Check if the selected degree level meets the requirement
    if (value !== requiredLevel) {
      // Check if this issue already exists
      if (!eligibilityIssues.some(issue => issue.includes('degree level requirement'))) {
        setEligibilityIssues(prevIssues => [...prevIssues, `This program requires a ${requiredLevel} degree, but you selected ${value}.`]);
        // Add recommendation
        setRecommendations(prevRecs => [...prevRecs, `Check if you qualify for an exception based on work experience or other qualifications.`]);
      }
    } else {
      // Remove the degree level issue if it exists
      setEligibilityIssues(prevIssues => prevIssues.filter(issue => !issue.includes('degree level requirement')));
      // Remove the related recommendation
      setRecommendations(prevRecs => prevRecs.filter(rec => !rec.includes('exception based on work experience')));
    }
  };
  // Validate English proficiency
  const validateEnglishProficiency = (type, score) => {
    if (!program) return;
    const scoreValue = parseFloat(score);
    if (isNaN(scoreValue)) return;
    const requirements = program.requirements.englishProficiency;
    if (type === 'IELTS' && scoreValue < requirements.ielts) {
      // Check if this issue already exists
      if (!eligibilityIssues.some(issue => issue.includes('English proficiency'))) {
        setEligibilityIssues(prevIssues => [...prevIssues, `Your IELTS score (${scoreValue}) is below the minimum requirement of ${requirements.ielts}.`]);
        // Add recommendation
        setRecommendations(prevRecs => [...prevRecs, `Consider taking an English preparation course or retaking the test.`]);
      }
    } else if (type === 'TOEFL' && scoreValue < requirements.toefl) {
      // Check if this issue already exists
      if (!eligibilityIssues.some(issue => issue.includes('English proficiency'))) {
        setEligibilityIssues(prevIssues => [...prevIssues, `Your TOEFL score (${scoreValue}) is below the minimum requirement of ${requirements.toefl}.`]);
        // Add recommendation
        setRecommendations(prevRecs => [...prevRecs, `Consider taking an English preparation course or retaking the test.`]);
      }
    } else {
      // Remove the English proficiency issue if it exists
      setEligibilityIssues(prevIssues => prevIssues.filter(issue => !issue.includes('English proficiency')));
      // Remove the related recommendation
      setRecommendations(prevRecs => prevRecs.filter(rec => !rec.includes('English preparation course')));
    }
  };
  // Validate the current step
  const validateStep = () => {
    const newErrors = {};
    if (currentStep === 0) {
      // Validate Personal Information
      const {
        personalInfo
      } = formData;
      if (!personalInfo.firstName.trim()) {
        newErrors['personalInfo.firstName'] = 'First name is required';
      }
      if (!personalInfo.lastName.trim()) {
        newErrors['personalInfo.lastName'] = 'Last name is required';
      }
      if (!personalInfo.email.trim()) {
        newErrors['personalInfo.email'] = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(personalInfo.email)) {
        newErrors['personalInfo.email'] = 'Email is invalid';
      }
      if (!personalInfo.phone.trim()) {
        newErrors['personalInfo.phone'] = 'Phone number is required';
      }
      if (!personalInfo.dateOfBirth) {
        newErrors['personalInfo.dateOfBirth'] = 'Date of birth is required';
      }
      if (!personalInfo.nationality.trim()) {
        newErrors['personalInfo.nationality'] = 'Nationality is required';
      }
      // Address validation
      if (!personalInfo.address.street.trim()) {
        newErrors['personalInfo.address.street'] = 'Street address is required';
      }
      if (!personalInfo.address.city.trim()) {
        newErrors['personalInfo.address.city'] = 'City is required';
      }
      if (!personalInfo.address.country.trim()) {
        newErrors['personalInfo.address.country'] = 'Country is required';
      }
    } else if (currentStep === 1) {
      // Validate Education
      formData.education.forEach((edu, index) => {
        if (!edu.institution.trim()) {
          newErrors[`education.${index}.institution`] = 'Institution is required';
        }
        if (!edu.degreeLevel) {
          newErrors[`education.${index}.degreeLevel`] = 'Degree level is required';
        }
        if (!edu.fieldOfStudy.trim()) {
          newErrors[`education.${index}.fieldOfStudy`] = 'Field of study is required';
        }
        if (!edu.startDate) {
          newErrors[`education.${index}.startDate`] = 'Start date is required';
        }
        if (!edu.gpa) {
          newErrors[`education.${index}.gpa`] = 'GPA is required';
        } else if (isNaN(parseFloat(edu.gpa)) || parseFloat(edu.gpa) <= 0) {
          newErrors[`education.${index}.gpa`] = 'GPA must be a positive number';
        }
        if (!edu.maxGpa) {
          newErrors[`education.${index}.maxGpa`] = 'Max GPA is required';
        } else if (isNaN(parseFloat(edu.maxGpa)) || parseFloat(edu.maxGpa) <= 0) {
          newErrors[`education.${index}.maxGpa`] = 'Max GPA must be a positive number';
        }
      });
    } else if (currentStep === 2) {
      // Validate Work Experience
      // Work experience might be optional, so we only validate filled fields
      formData.workExperience.forEach((exp, index) => {
        if (exp.company.trim() || exp.position.trim() || exp.startDate || exp.description.trim()) {
          // If any field is filled, all required fields must be filled
          if (!exp.company.trim()) {
            newErrors[`workExperience.${index}.company`] = 'Company is required';
          }
          if (!exp.position.trim()) {
            newErrors[`workExperience.${index}.position`] = 'Position is required';
          }
          if (!exp.startDate) {
            newErrors[`workExperience.${index}.startDate`] = 'Start date is required';
          }
        }
      });
    } else if (currentStep === 3) {
      // Validate Documents
      if (program) {
        const requiredDocuments = program.requirements.requiredDocuments;
        // Check for required documents
        requiredDocuments.forEach(doc => {
          const documentKey = doc.replace(/\s+/g, '');
          if (!formData.documents[documentKey] && !userProfile.documents.some(d => d.name.toLowerCase().includes(doc.toLowerCase()))) {
            newErrors[`documents.${documentKey}`] = `${doc} is required`;
          }
        });
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle next step
  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  // Handle previous step
  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };
  // Handle save as draft
  const handleSaveAsDraft = () => {
    // In a real app, you would save the form data to a database
    toast.success('Application saved as draft', {
      duration: 3000,
      icon: <SaveIcon className="w-5 h-5" data-id="element-3391" />
    });
  };
  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    // Validate the final step
    if (validateStep()) {
      // In a real app, you would submit the form data to a server
      toast.success('Application submitted successfully', {
        duration: 3000,
        icon: <CheckCircleIcon className="w-5 h-5" data-id="element-3392" />
      });
      // Navigate to confirmation page
      navigate('/student/application-confirmation');
    }
  };
  // Render loading state
  if (loading) {
    return <Layout persona="Student" data-id="element-3393">
        <div className="flex justify-center items-center h-64" data-id="element-3394">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" data-id="element-3395"></div>
        </div>
      </Layout>;
  }
  // Render program not found
  if (!program) {
    return <Layout persona="Student" data-id="element-3396">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm text-center" data-id="element-3397">
          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-3398">
            <GraduationCapIcon className="w-8 h-8 text-gray-400" data-id="element-3399" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-3400">
            Program Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-3401">
            The program you're applying for doesn't exist or has been removed.
          </p>
          <button onClick={() => navigate('/student/programs')} className="bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors" data-id="element-3402">
            Back to Programs
          </button>
        </div>
      </Layout>;
  }
  return <Layout persona="Student" data-id="element-3403">
      <div className="max-w-4xl mx-auto" data-id="element-3404">
        {/* Back button */}
        <div className="mb-6" data-id="element-3405">
          <button onClick={() => navigate(`/student/programs/${programId}`)} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-3406">
            <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-3407" />
            Back to Program Details
          </button>
        </div>
        {/* Application header */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8" data-id="element-3408">
          <h1 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-3409">
            Application for {program.name}
          </h1>
          <div className="flex items-center text-gray-600 mb-4" data-id="element-3410">
            <BuildingIcon className="w-4 h-4 mr-1.5" data-id="element-3411" />
            <span data-id="element-3412">{program.university}</span>
          </div>
          <div className="flex flex-wrap gap-4 text-sm" data-id="element-3413">
            <div className="flex items-center" data-id="element-3414">
              <GraduationCapIcon className="w-4 h-4 mr-1.5 text-gray-500" data-id="element-3415" />
              <span data-id="element-3416">{program.type}</span>
            </div>
            <div className="flex items-center" data-id="element-3417">
              <CalendarIcon className="w-4 h-4 mr-1.5 text-gray-500" data-id="element-3418" />
              <span data-id="element-3419">Duration: {program.duration}</span>
            </div>
            <div className="flex items-center" data-id="element-3420">
              <CalendarIcon className="w-4 h-4 mr-1.5 text-gray-500" data-id="element-3421" />
              <span data-id="element-3422">Deadline: {program.applicationDeadline}</span>
            </div>
            <div className="flex items-center" data-id="element-3423">
              <MapPinIcon className="w-4 h-4 mr-1.5 text-gray-500" data-id="element-3424" />
              <span data-id="element-3425">{program.location}</span>
            </div>
          </div>
        </div>
        {/* Progress steps */}
        <ProgressStep currentStep={currentStep} totalSteps={steps.length} steps={steps} data-id="element-3426" />
        {/* AI Recommendations */}
        {showRecommendations && recommendations.length > 0 && <AIRecommendation recommendations={recommendations} onClose={() => setShowRecommendations(false)} data-id="element-3427" />}
        {/* Eligibility Alert */}
        {eligibilityIssues.length > 0 && <EligibilityAlert isEligible={eligibilityIssues.length <= 1} issues={eligibilityIssues} data-id="element-3428" />}
        {/* Application Form */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8" data-id="element-3429">
          <form onSubmit={handleSubmit} data-id="element-3430">
            {/* Step 1: Personal Information */}
            {currentStep === 0 && <div data-id="element-3431">
                <h2 className="text-xl font-semibold text-gray-800 mb-6" data-id="element-3432">
                  Personal Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-3433">
                  <FormField label="First Name" name="firstName" value={formData.personalInfo.firstName} onChange={e => handleChange('personalInfo', 'firstName', e.target.value)} required error={errors['personalInfo.firstName']} data-id="element-3434" />
                  <FormField label="Last Name" name="lastName" value={formData.personalInfo.lastName} onChange={e => handleChange('personalInfo', 'lastName', e.target.value)} required error={errors['personalInfo.lastName']} data-id="element-3435" />
                  <FormField label="Email" type="email" name="email" value={formData.personalInfo.email} onChange={e => handleChange('personalInfo', 'email', e.target.value)} required error={errors['personalInfo.email']} data-id="element-3436" />
                  <FormField label="Phone Number" type="tel" name="phone" value={formData.personalInfo.phone} onChange={e => handleChange('personalInfo', 'phone', e.target.value)} required error={errors['personalInfo.phone']} data-id="element-3437" />
                  <FormField label="Date of Birth" type="date" name="dateOfBirth" value={formData.personalInfo.dateOfBirth} onChange={e => handleChange('personalInfo', 'dateOfBirth', e.target.value)} required error={errors['personalInfo.dateOfBirth']} data-id="element-3438" />
                  <FormField label="Nationality" name="nationality" value={formData.personalInfo.nationality} onChange={e => handleChange('personalInfo', 'nationality', e.target.value)} required error={errors['personalInfo.nationality']} data-id="element-3439" />
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-3440">
                  Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3441">
                  <div className="md:col-span-2" data-id="element-3442">
                    <FormField label="Street Address" name="street" value={formData.personalInfo.address.street} onChange={e => handleNestedChange('personalInfo', 'address', 'street', e.target.value)} required error={errors['personalInfo.address.street']} data-id="element-3443" />
                  </div>
                  <FormField label="City" name="city" value={formData.personalInfo.address.city} onChange={e => handleNestedChange('personalInfo', 'address', 'city', e.target.value)} required error={errors['personalInfo.address.city']} data-id="element-3444" />
                  <FormField label="Country" name="country" value={formData.personalInfo.address.country} onChange={e => handleNestedChange('personalInfo', 'address', 'country', e.target.value)} required error={errors['personalInfo.address.country']} data-id="element-3445" />
                  <FormField label="Postal Code" name="postalCode" value={formData.personalInfo.address.postalCode} onChange={e => handleNestedChange('personalInfo', 'address', 'postalCode', e.target.value)} error={errors['personalInfo.address.postalCode']} data-id="element-3446" />
                </div>
              </div>}
            {/* Step 2: Academic Background */}
            {currentStep === 1 && <div data-id="element-3447">
                <h2 className="text-xl font-semibold text-gray-800 mb-6" data-id="element-3448">
                  Academic Background
                </h2>
                {formData.education.map((education, index) => <EducationItem key={index} education={education} index={index} onChange={handleEducationChange} onRemove={removeEducation} errors={errors} data-id="element-3449" />)}
                <button type="button" onClick={addEducation} className="mt-2 px-4 py-2 border border-primary-500 text-primary-500 rounded-md hover:bg-primary-50 transition-colors flex items-center" data-id="element-3450">
                  <PlusCircleIcon className="w-4 h-4 mr-2" data-id="element-3451" />
                  Add Another Education
                </button>
              </div>}
            {/* Step 3: Work Experience */}
            {currentStep === 2 && <div data-id="element-3452">
                <h2 className="text-xl font-semibold text-gray-800 mb-6" data-id="element-3453">
                  Work Experience
                </h2>
                {formData.workExperience.map((experience, index) => <WorkExperienceItem key={index} experience={experience} index={index} onChange={handleWorkExperienceChange} onRemove={removeWorkExperience} errors={errors} data-id="element-3454" />)}
                <button type="button" onClick={addWorkExperience} className="mt-2 px-4 py-2 border border-primary-500 text-primary-500 rounded-md hover:bg-primary-50 transition-colors flex items-center" data-id="element-3455">
                  <PlusCircleIcon className="w-4 h-4 mr-2" data-id="element-3456" />
                  Add Another Experience
                </button>
              </div>}
            {/* Step 4: Documents */}
            {currentStep === 3 && <div data-id="element-3457">
                <h2 className="text-xl font-semibold text-gray-800 mb-6" data-id="element-3458">
                  Required Documents
                </h2>
                <p className="text-gray-600 mb-6" data-id="element-3459">
                  Please upload the following documents. Accepted formats: PDF,
                  JPG, PNG (Max. 5MB each).
                </p>
                {program.requirements.requiredDocuments.map((doc, index) => {
              const documentKey = doc.replace(/\s+/g, '');
              const existingDoc = userProfile.documents.find(d => d.name.toLowerCase().includes(doc.toLowerCase()));
              return <DocumentUpload key={index} label={doc} required={true} description={`Upload your ${doc}`} onUpload={file => handleDocumentUpload(documentKey, file)} existingDocument={existingDoc} documents={formData.documents} data-id="element-3460" />;
            })}
                <div className="mt-6" data-id="element-3461">
                  <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-3462">
                    Additional Documents (Optional)
                  </h3>
                  <DocumentUpload label="Additional Document" required={false} description="Upload any additional documents that may support your application" onUpload={file => handleDocumentUpload('additional', file)} data-id="element-3463" />
                </div>
              </div>}
            {/* Step 5: Review & Submit */}
            {currentStep === 4 && <div data-id="element-3464">
                <h2 className="text-xl font-semibold text-gray-800 mb-6" data-id="element-3465">
                  Review & Submit
                </h2>
                <div className="space-y-6" data-id="element-3466">
                  {/* Personal Information Review */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-3467">
                    <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-3468">
                      <h3 className="text-lg font-medium text-gray-800" data-id="element-3469">
                        Personal Information
                      </h3>
                      <button type="button" onClick={() => setCurrentStep(0)} className="text-primary-500 hover:text-primary-700 text-sm font-medium" data-id="element-3470">
                        Edit
                      </button>
                    </div>
                    <div className="p-4" data-id="element-3471">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3472">
                        <div data-id="element-3473">
                          <p className="text-sm text-gray-500" data-id="element-3474">Full Name</p>
                          <p className="font-medium" data-id="element-3475">
                            {formData.personalInfo.firstName}{' '}
                            {formData.personalInfo.lastName}
                          </p>
                        </div>
                        <div data-id="element-3476">
                          <p className="text-sm text-gray-500" data-id="element-3477">Email</p>
                          <p className="font-medium" data-id="element-3478">
                            {formData.personalInfo.email}
                          </p>
                        </div>
                        <div data-id="element-3479">
                          <p className="text-sm text-gray-500" data-id="element-3480">Phone</p>
                          <p className="font-medium" data-id="element-3481">
                            {formData.personalInfo.phone}
                          </p>
                        </div>
                        <div data-id="element-3482">
                          <p className="text-sm text-gray-500" data-id="element-3483">Date of Birth</p>
                          <p className="font-medium" data-id="element-3484">
                            {formData.personalInfo.dateOfBirth}
                          </p>
                        </div>
                        <div data-id="element-3485">
                          <p className="text-sm text-gray-500" data-id="element-3486">Nationality</p>
                          <p className="font-medium" data-id="element-3487">
                            {formData.personalInfo.nationality}
                          </p>
                        </div>
                        <div data-id="element-3488">
                          <p className="text-sm text-gray-500" data-id="element-3489">Address</p>
                          <p className="font-medium" data-id="element-3490">
                            {formData.personalInfo.address.street},{' '}
                            {formData.personalInfo.address.city},{' '}
                            {formData.personalInfo.address.country}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Education Review */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-3491">
                    <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-3492">
                      <h3 className="text-lg font-medium text-gray-800" data-id="element-3493">
                        Education
                      </h3>
                      <button type="button" onClick={() => setCurrentStep(1)} className="text-primary-500 hover:text-primary-700 text-sm font-medium" data-id="element-3494">
                        Edit
                      </button>
                    </div>
                    <div className="p-4" data-id="element-3495">
                      {formData.education.map((edu, index) => <div key={index} className={`${index > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}`} data-id="element-3496">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3497">
                            <div data-id="element-3498">
                              <p className="text-sm text-gray-500" data-id="element-3499">
                                Institution
                              </p>
                              <p className="font-medium" data-id="element-3500">{edu.institution}</p>
                            </div>
                            <div data-id="element-3501">
                              <p className="text-sm text-gray-500" data-id="element-3502">Degree</p>
                              <p className="font-medium" data-id="element-3503">
                                {edu.degreeLevel} in {edu.fieldOfStudy}
                              </p>
                            </div>
                            <div data-id="element-3504">
                              <p className="text-sm text-gray-500" data-id="element-3505">Dates</p>
                              <p className="font-medium" data-id="element-3506">
                                {edu.startDate} - {edu.endDate || 'Present'}
                              </p>
                            </div>
                            <div data-id="element-3507">
                              <p className="text-sm text-gray-500" data-id="element-3508">GPA</p>
                              <p className="font-medium" data-id="element-3509">
                                {edu.gpa} / {edu.maxGpa}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>
                  {/* Work Experience Review */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-3510">
                    <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-3511">
                      <h3 className="text-lg font-medium text-gray-800" data-id="element-3512">
                        Work Experience
                      </h3>
                      <button type="button" onClick={() => setCurrentStep(2)} className="text-primary-500 hover:text-primary-700 text-sm font-medium" data-id="element-3513">
                        Edit
                      </button>
                    </div>
                    <div className="p-4" data-id="element-3514">
                      {formData.workExperience.length > 0 && formData.workExperience[0].company ? formData.workExperience.map((exp, index) => <div key={index} className={`${index > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}`} data-id="element-3515">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3516">
                              <div data-id="element-3517">
                                <p className="text-sm text-gray-500" data-id="element-3518">Company</p>
                                <p className="font-medium" data-id="element-3519">{exp.company}</p>
                              </div>
                              <div data-id="element-3520">
                                <p className="text-sm text-gray-500" data-id="element-3521">
                                  Position
                                </p>
                                <p className="font-medium" data-id="element-3522">{exp.position}</p>
                              </div>
                              <div data-id="element-3523">
                                <p className="text-sm text-gray-500" data-id="element-3524">Dates</p>
                                <p className="font-medium" data-id="element-3525">
                                  {exp.startDate} - {exp.endDate || 'Present'}
                                </p>
                              </div>
                              <div className="md:col-span-2" data-id="element-3526">
                                <p className="text-sm text-gray-500" data-id="element-3527">
                                  Description
                                </p>
                                <p className="font-medium" data-id="element-3528">{exp.description}</p>
                              </div>
                            </div>
                          </div>) : <p className="text-gray-500 italic" data-id="element-3529">
                          No work experience provided
                        </p>}
                    </div>
                  </div>
                  {/* Documents Review */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-3530">
                    <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-3531">
                      <h3 className="text-lg font-medium text-gray-800" data-id="element-3532">
                        Documents
                      </h3>
                      <button type="button" onClick={() => setCurrentStep(3)} className="text-primary-500 hover:text-primary-700 text-sm font-medium" data-id="element-3533">
                        Edit
                      </button>
                    </div>
                    <div className="p-4" data-id="element-3534">
                      <div className="space-y-2" data-id="element-3535">
                        {program.requirements.requiredDocuments.map((doc, index) => {
                      const documentKey = doc.replace(/\s+/g, '');
                      const uploadedDoc = formData.documents[documentKey];
                      const existingDoc = userProfile.documents.find(d => d.name.toLowerCase().includes(doc.toLowerCase()));
                      return <div key={index} className="flex items-center" data-id="element-3536">
                                {uploadedDoc || existingDoc ? <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-3537" /> : <AlertCircleIcon className="w-5 h-5 text-red-500 mr-2" data-id="element-3538" />}
                                <span className="text-gray-700" data-id="element-3539">{doc}</span>
                                {uploadedDoc && <span className="ml-2 text-sm text-gray-500" data-id="element-3540">
                                    ({uploadedDoc.name})
                                  </span>}
                                {existingDoc && <span className="ml-2 text-sm text-gray-500" data-id="element-3541">
                                    (Using existing document)
                                  </span>}
                              </div>;
                    })}
                        {formData.documents.additional && <div className="flex items-center" data-id="element-3542">
                            <PaperclipIcon className="w-5 h-5 text-gray-500 mr-2" data-id="element-3543" />
                            <span className="text-gray-700" data-id="element-3544">
                              Additional Document
                            </span>
                            <span className="ml-2 text-sm text-gray-500" data-id="element-3545">
                              ({formData.documents.additional.name})
                            </span>
                          </div>}
                      </div>
                    </div>
                  </div>
                  {/* English Proficiency section moved to the Review step */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-3546">
                    <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-3547">
                      <h3 className="text-lg font-medium text-gray-800" data-id="element-3548">
                        English Proficiency
                      </h3>
                      <button type="button" onClick={() => {
                    // Open a modal or form to edit English proficiency
                    toast.info('Edit English proficiency functionality coming soon', {
                      duration: 3000,
                      icon: <InfoIcon className="w-5 h-5" data-id="element-3550" />
                    });
                  }} className="text-primary-500 hover:text-primary-700 text-sm font-medium" data-id="element-3549">
                        Edit
                      </button>
                    </div>
                    <div className="p-4" data-id="element-3551">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3552">
                        <div data-id="element-3553">
                          <p className="text-sm text-gray-500" data-id="element-3554">Test Type</p>
                          <p className="font-medium" data-id="element-3555">
                            {formData.englishProficiency.type || 'Not provided'}
                          </p>
                        </div>
                        <div data-id="element-3556">
                          <p className="text-sm text-gray-500" data-id="element-3557">Score</p>
                          <p className="font-medium" data-id="element-3558">
                            {formData.englishProficiency.score || 'Not provided'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Declaration */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-3559">
                    <div className="flex items-start" data-id="element-3560">
                      <div className="flex items-center h-5" data-id="element-3561">
                        <input id="declaration" name="declaration" type="checkbox" className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" required data-id="element-3562" />
                      </div>
                      <label htmlFor="declaration" className="ml-3 text-sm text-gray-700" data-id="element-3563">
                        I confirm that the information provided in this
                        application is true, complete, and accurate. I
                        understand that any false or misleading information may
                        result in the rejection of my application or subsequent
                        termination of my enrollment.
                      </label>
                    </div>
                  </div>
                </div>
              </div>}
            {/* Form Navigation */}
            <div className="mt-8 flex justify-between" data-id="element-3564">
              {currentStep > 0 ? <button type="button" onClick={handlePrevious} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center" data-id="element-3565">
                  <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-3566" />
                  Previous
                </button> : <div data-id="element-3567"></div>}
              <div className="flex space-x-3" data-id="element-3568">
                <button type="button" onClick={handleSaveAsDraft} className="px-4 py-2 border border-primary-500 text-primary-500 rounded-md hover:bg-primary-50 transition-colors flex items-center" data-id="element-3569">
                  <SaveIcon className="w-4 h-4 mr-2" data-id="element-3570" />
                  Save as Draft
                </button>
                {currentStep < steps.length - 1 ? <button type="button" onClick={handleNext} className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors flex items-center" data-id="element-3571">
                    Next
                    <ChevronRightIcon className="w-4 h-4 ml-2" data-id="element-3572" />
                  </button> : <button type="submit" className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors flex items-center" data-id="element-3573">
                    <SendIcon className="w-4 h-4 mr-2" data-id="element-3574" />
                    Submit Application
                  </button>}
              </div>
            </div>
          </form>
        </div>
      </div>
    </Layout>;
};
export default ApplicationForm;